import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { getEstimatedViewportType } from "@faire/web--source/common/user-agent/getEstimatedViewportType";
import { createContextStore } from "@faire/web--source/ui/hooks/ContextStore";
import { useState } from "@faire/web--source/ui/hooks/useState";
import React, { useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { useSyncExternalStore } from "use-sync-external-store/shim";
import { isTouchDevice as checkIsTouchDevice } from "../../common/isTouchDevice";
export var getWindowWidth = function() {
    var _getWindow;
    var _getWindow_innerWidth;
    return (_getWindow_innerWidth = (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.innerWidth) !== null && _getWindow_innerWidth !== void 0 ? _getWindow_innerWidth : 0;
};
export var getWindowHeight = function() {
    var _getWindow;
    var _getWindow_innerHeight;
    return (_getWindow_innerHeight = (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.innerHeight) !== null && _getWindow_innerHeight !== void 0 ? _getWindow_innerHeight : 0;
};
var _createContextStore = createContextStore({
    width: 0,
    height: 0,
    isTouchDevice: false,
    isWindowFocused: false,
    isOnline: false
}, {
    name: "ObservableWindowStore"
}), Provider = _createContextStore.Provider, useStore = _createContextStore.useStore;
/**
 * This hook should be instantiated once at the root of the application.
 * Instantiating multiple times may cause performance issues.
 */ export var ObservableWindowStoreProvider = function(param) {
    var children = param.children;
    // Why are we using `useState` here? See: https://fairewholesale.atlassian.net/browse/FD-128466
    var _useState = _sliced_to_array(useState(useIsSSR()), 1), isSSR = _useState[0];
    return /*#__PURE__*/ _jsx(ProviderWithInitialState, {
        isSSR: isSSR,
        children: children
    });
};
var ProviderWithInitialState = /*#__PURE__*/ React.memo(function(param) {
    var children = param.children, isSSR = param.isSSR;
    var _useState = _sliced_to_array(useState(getEstimatedViewportType().width), 2), width = _useState[0], setWidth = _useState[1];
    var _useState1 = _sliced_to_array(useState(getEstimatedViewportType().height), 2), height = _useState1[0], setHeight = _useState1[1];
    // accessing getWindowWidth() causes animation jank
    // so we only read that in LayoutEffect
    useLayoutEffect(function() {
        if (isSSR) {
            var _getEstimatedViewportType = getEstimatedViewportType(), width = _getEstimatedViewportType.width, height = _getEstimatedViewportType.height;
            setWidth(width);
            setHeight(height);
        } else {
            setWidth(getWindowWidth());
            setHeight(getWindowHeight());
        }
    }, [
        isSSR
    ]);
    var isTouchDevice = isSSR ? false : checkIsTouchDevice();
    var isWindowFocused = true;
    var isOnline = true;
    return /*#__PURE__*/ _jsxs(Provider, {
        value: {
            width: width,
            height: height,
            isTouchDevice: isTouchDevice,
            isWindowFocused: isWindowFocused,
            isOnline: isOnline
        },
        children: [
            /*#__PURE__*/ _jsx(InitializeObservableWindowStore, {}),
            children
        ]
    });
});
/**
 * Set up event handlers to update the store.
 * This also corrects the SSR viewport mismatch as a post-hydration step.
 */ var InitializeObservableWindowStore = function() {
    var _useStore = _sliced_to_array(useStore([]), 2), _ = _useStore[0], setStore = _useStore[1];
    var setDimensions = useCallback(function() {
        setStore(function() {
            return {
                width: getWindowWidth(),
                height: getWindowHeight()
            };
        });
    }, [
        setStore
    ]);
    var handleResize = useMemo(function() {
        return debounceIfPossible(setDimensions, 200);
    }, [
        setDimensions
    ]);
    var handleTouch = useCallback(function() {
        return setStore({
            isTouchDevice: true
        });
    }, [
        setStore
    ]);
    var handleFocus = useCallback(function() {
        return setStore({
            isWindowFocused: true
        });
    }, [
        setStore
    ]);
    var handleBlur = useCallback(function() {
        return setStore({
            isWindowFocused: false
        });
    }, [
        setStore
    ]);
    var handleOnline = useCallback(function() {
        return setStore({
            isOnline: true
        });
    }, [
        setStore
    ]);
    var handleOffline = useCallback(function() {
        return setStore({
            isOnline: false
        });
    }, [
        setStore
    ]);
    useEffect(function() {
        setStore(function() {
            return {
                width: getWindowWidth(),
                height: getWindowHeight(),
                isTouchDevice: checkIsTouchDevice()
            };
        });
    }, [
        setStore
    ]);
    useEffect(function() {
        var _getWindow, _getWindow1, _getWindow2, _getWindow3, _getWindow4, _getWindow5;
        (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.addEventListener("resize", handleResize, false);
        (_getWindow1 = getWindow()) === null || _getWindow1 === void 0 ? void 0 : _getWindow1.addEventListener("touchstart", handleTouch);
        (_getWindow2 = getWindow()) === null || _getWindow2 === void 0 ? void 0 : _getWindow2.addEventListener("focus", handleFocus);
        (_getWindow3 = getWindow()) === null || _getWindow3 === void 0 ? void 0 : _getWindow3.addEventListener("blur", handleBlur);
        (_getWindow4 = getWindow()) === null || _getWindow4 === void 0 ? void 0 : _getWindow4.addEventListener("online", handleOnline);
        (_getWindow5 = getWindow()) === null || _getWindow5 === void 0 ? void 0 : _getWindow5.addEventListener("offline", handleOffline);
        return function() {
            var _getWindow, _getWindow1, _getWindow2, _getWindow3, _getWindow4, _getWindow5;
            (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.removeEventListener("resize", handleResize, false);
            (_getWindow1 = getWindow()) === null || _getWindow1 === void 0 ? void 0 : _getWindow1.removeEventListener("touchstart", handleTouch);
            (_getWindow2 = getWindow()) === null || _getWindow2 === void 0 ? void 0 : _getWindow2.removeEventListener("focus", handleFocus, false);
            (_getWindow3 = getWindow()) === null || _getWindow3 === void 0 ? void 0 : _getWindow3.removeEventListener("blur", handleBlur, false);
            (_getWindow4 = getWindow()) === null || _getWindow4 === void 0 ? void 0 : _getWindow4.removeEventListener("online", handleOnline, false);
            (_getWindow5 = getWindow()) === null || _getWindow5 === void 0 ? void 0 : _getWindow5.removeEventListener("offline", handleOffline, false);
        };
    }, [
        handleBlur,
        handleFocus,
        handleOffline,
        handleOnline,
        handleResize,
        handleTouch
    ]);
    return null;
};
/**
 * Make sure you wrap the root of app with ObservableWindowStoreProvider before using this hook.
 * Otherwise, you will receive an error.
 */ export var useObservableWindow = function() {
    var _useStore = _sliced_to_array(useStore([
        "width",
        "height",
        "isTouchDevice",
        "isWindowFocused",
        "isOnline"
    ]), 1), clientObservableWindow = _useStore[0];
    var defaultServerObservableWindow = useMemo(getDefaultServerWindow, []);
    // client viewport correction effect can happen prior to the delayed hydration of boundary components
    // when the delayed hydration reads the store, we should return the default SSR value
    // when hydration completes, useSyncExternalStore will re-render the component with the correct value
    return useSyncExternalStore(function() {
        return function() {};
    }, function() {
        return clientObservableWindow;
    }, function() {
        return defaultServerObservableWindow;
    });
};
var getDefaultServerWindow = function() {
    var _getEstimatedViewportType = getEstimatedViewportType(), width = _getEstimatedViewportType.width, height = _getEstimatedViewportType.height;
    return {
        width: width,
        height: height,
        isTouchDevice: false,
        isWindowFocused: true,
        isOnline: true
    };
};
